import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  Avatar,
  Stack,
  Container,
  Tooltip,
  Button,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { IoSend } from "react-icons/io5";
import Iconify from "src/components/Iconify";
import { addServerRequest, serverRequest } from "src/api/servers";
import { deepOrange, green } from '@mui/material/colors';

const ChatContainer = styled(Paper)(({ theme }) => ({
  height: "100vh",
  width: "500px",
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
}));

const MessagesContainer = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "20px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "3px",
  },
});

const MessageBubble = styled(Box, { shouldForwardProp: (prop) => prop !== "isuser" })(
  ({ isuser }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: "16px",
  flexDirection: isuser ? "row-reverse" : "row",
}));

const MessageContent = styled(Paper, { shouldForwardProp: (prop) => prop !== "isuser" })(
  ({ isuser }) => ({
  padding: "12px 16px",
  borderRadius: "16px",
  maxWidth: "70%",
  marginLeft: isuser ? 0 : "12px",
  marginRight: isuser ? "12px" : 0,
  backgroundColor: isuser ? "#2196f3" : "#f5f5f5",
  color: isuser ? "#fff" : "#000",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    transform: "scale(1.02)",
  },
}));

const InputContainer = styled(Box)({
  padding: "20px",
  borderTop: "1px solid rgba(0, 0, 0, 0.1)",
});


const ServerRequestChatBox = ({onClose, serverActiveData}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getServerData = () => {
    serverRequest(serverActiveData.ServerId, (response) => {
      if (response.status === 200) {
        // console.log("resposne server",response.data);
        setMessages(response.data)
      }
    })
  }

  useEffect(() => {
    if (serverActiveData && serverActiveData.hasOwnProperty("ServerId") && serverActiveData.ServerId) {
      getServerData()
    }    
  }, [serverActiveData]);

  const handleSendMessage = () => {
    if (newMessage.trim() && serverActiveData.Id) {
      addServerRequest({
        ServerId: serverActiveData.Id,
        ShellCommand: newMessage
      }, (response) =>{
        if ([200, 201].includes(response.status)) {
          getServerData()
          setNewMessage("");
        }  
      })      
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const [isResponseCopy, setResponseCopy] = useState(false)

  const handleResponseCopy = (copyText, id) => {
      navigator.clipboard.writeText(copyText).then(() => {
        setResponseCopy(id);
        setTimeout(() => setResponseCopy(false), 2000); // Reset copied state after 2 seconds
      }).catch(err => {
          console.error('Failed to copy: ', err);
      });
  };

  return (
    <ChatContainer>
      

<Stack
  direction="row"
  spacing={2}
  sx={{
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #f1f1f1",
    py: 1, 
    px: 2
  }}
>
  <Box>
    <Typography variant="h5">{serverActiveData.hasOwnProperty("ServerName") ? serverActiveData.ServerName: ""}</Typography>
    <Typography sx={{color: serverActiveData.hasOwnProperty("MonitoringStatus") && Number(serverActiveData.MonitoringStatus) === 1 ? "green": "red"}}>{serverActiveData.hasOwnProperty("MonitoringStatus") && Number(serverActiveData.MonitoringStatus) === 1 ? "Online": "Offline"}</Typography>
  </Box>  
  <IconButton onClick={onClose('right', false, {})}>
    <Iconify icon="eva:close-outline" />
  </IconButton>
</Stack>
    
      <MessagesContainer>
        {messages.map((message) => (
          <Box key={message.Id}>
            <MessageBubble isuser={true}>
              {message.RequestedBy ? (<Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: green[500]
                }}
              >{message.RequestedBy[0]}</Avatar>): (<Avatar
                alt="No Person"
                sx={{
                  width: 40,
                  height: 40
                }}
              />)}
              <MessageContent isuser={true}>                
                <Typography variant="body1" component="div" sx={{wordWrap: "break-word"}}>
                  {message.ShellCommand}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, mt: 0.5, display: "block" }}
                >
                  {message.CreatedOn}
                </Typography>
              </MessageContent>
            </MessageBubble>
            {message.Response && (
              <MessageBubble isuser={false}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: deepOrange[500]
                }}
              >{message.ServerName[0]}</Avatar>
              <MessageContent isuser={false}>
                <Box sx={{textAlign: "right"}}>
                  {isResponseCopy === message.Id  ? (
                    <Chip color="success" label="Copied" size="small" variant="outlined" />
                  ) : (
                    <Chip label="Copy" size="small" variant="outlined" onClick={() => handleResponseCopy(message.Response, message.Id)} />
                  )}
                  
                </Box>                
                <Typography variant="body1" component="div" sx={{wordWrap: "break-word"}}>
                  {message.Response ? message.Response.slice(0, 100): ''} {message.Response.length > 100? (<Tooltip title="Sorry! More option are not available at the moment. Please use the copy option to copy the text.">...</Tooltip>): ""}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.7, mt: 0.5, display: "block" }}
                >
                  {message.CreatedOn}
                </Typography>
              </MessageContent>
            </MessageBubble>
            )}
            
          </Box>          
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      <InputContainer>
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            variant="outlined"
            size="small"
            sx={{ backgroundColor: "background.paper" }}
            aria-label="Message input field"
          />
          <IconButton
            onClick={handleSendMessage}
            color="primary"
            aria-label="Send message"
            sx={{
              backgroundColor: "#2196f3",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <IoSend />
          </IconButton>
        </Stack>
      </InputContainer>
    </ChatContainer>
  );
};

export default ServerRequestChatBox;